/**
 * `store/persist` — the four-tier persistence of `spec.md` §13.7.
 *
 * | Tier | Key | What | Why |
 * |---|---|---|---|
 * | server | `PUT /api/playback` | the whole resume state | the only tier we can trust |
 * | Telegram `CloudStorage` (6.9+) | `np_v1` | `{v,t,p,q,i,at}` | cross-device, **4096 chars max** |
 * | Telegram `DeviceStorage` (9.0+) | `queue_v1` | the queue snapshot | 5 MB, device-local |
 * | `localStorage` | `np_v1`, `prefs_v1` | a fast mirror + UI prefs | a cache, and it can *throw* |
 *
 * The one preference that does not stay device-local is the skin: `lib/skin` mirrors it
 * to `CloudStorage` under its own key, so a chosen skin follows the account. This file
 * still owns the fast local copy, because that is the tier the pre-boot script reads.
 *
 * ## Why this file takes a snapshot getter instead of importing the stores
 *
 * The plan is explicit: `persist.ts` must not import the engine, and it does not import
 * the stores either. It is a **sink**: `createPersistence({ getSnapshot })` is handed a
 * callback and knows nothing about where the numbers came from. That keeps the whole
 * module testable with a literal object, keeps the import graph acyclic, and means
 * `wiring.ts` (T37) stays the single place where the engine, the stores and this sink are
 * connected.
 *
 * ## The 4096-character guard
 *
 * `CloudStorage` caps a **value** at 4096 characters, allows 1024 keys, and restricts
 * keys to `[A-Za-z0-9_-]{1,128}`. A serialised *queue* — rows with titles, performers and
 * blur thumbnails — does not come close to fitting, which is why only the pointer goes
 * here at all. Even the bare id list runs out of room: 500 six-digit ids is ~3.5 KB and
 * survives, 1000 of them (`QUEUE_MAX_ITEMS`) does not.
 *
 * So the payload is built, **measured**, and degraded: first by dropping `q` (the id
 * list), leaving the pointer itself. The full queue always goes to `DeviceStorage`, which
 * is the tier sized for it. A write that still would not fit is skipped rather than
 * attempted — Telegram's own failure mode here is silent.
 *
 * (`.length` is UTF-16 code units, not code points. The payload is digits, commas and
 * braces, so the two coincide; the guard is nonetheless a `<=` on the serialised string
 * that Telegram will actually receive.)
 *
 * ## Throttling and flush points
 *
 * §13.7: cloud writes are rate-limited to one per ~15 s "plus one on flush". The flush
 * points are `pause`, `ended`, Telegram `deactivated`, `visibilitychange` → hidden,
 * `pagehide`, and a 10 s interval while playing — **never** `timeupdate`, which fires
 * about four times a second.
 *
 * Two of those points are DOM events with no relationship to audio, so
 * {@link Persistence.installFlushListeners} owns them here. The other three are engine and
 * Telegram events, so `wiring.ts` calls `save({ flush: true })` from those handlers —
 * and, per §13.3, those handlers touch **nothing** on the audio element.
 *
 * `localStorage` is written **synchronously, before the first `await`** of every save.
 * `pagehide` in a webview may be the last code that ever runs, and there is no
 * `beforeunload` to trust; the synchronous mirror is what survives it.
 *
 * ## The queue is not re-PUT from here
 *
 * §13.7 names `/api/queue` as part of the server tier, but the queue is *written* by the
 * mutations in `store/queue` (`PUT /api/queue` is a `DELETE` + bulk `INSERT` in one
 * transaction). Re-sending it on a 15 s timer would rewrite every row and churn the ids
 * for no gain. This sink persists the *pointer* — track, position, index, shuffle, repeat,
 * volume — which is what a resume actually needs.
 */

import type { RepeatMode, TrackSummary } from '@bl/shared';

import type { ApiClient, ApiFailure } from '../lib/api';
import { api, toApiFailure } from '../lib/api';
import type { CloudStorageAdapter, DeviceStorageAdapter } from '../lib/telegram';
import { telegram } from '../lib/telegram';

/* ────────────────────────────────────────────────────────────────────────────
 * Keys, limits and payload shapes
 * ──────────────────────────────────────────────────────────────────────────── */

/** The `CloudStorage` key of §13.7. Charset-legal: `[A-Za-z0-9_-]{1,128}`. */
export const RESUME_KEY = 'np_v1';

/** `DeviceStorage` (9.0+) holds what the cloud tier cannot. */
export const QUEUE_KEY = 'queue_v1';

/** UI prefs — `localStorage` only; they are per-device by nature. */
export const PREFS_KEY = 'bl_prefs_v1';

/** Telegram's documented per-value cap. */
export const CLOUD_VALUE_MAX_CHARS = 4096;

/** §13.7: "Rate-limit writes to one per ~15 s plus one on flush." */
export const CLOUD_THROTTLE_MS = 15_000;

/** `PUT /api/playback` is rate-limited 60/min per user (§6.6); 10 s is the interval. */
export const SERVER_THROTTLE_MS = 10_000;

/** §13.7: "a 10 s interval while playing". */
export const FLUSH_INTERVAL_MS = 10_000;

/** Bumped only on a breaking payload change; an unknown `v` is ignored, never guessed. */
export const SNAPSHOT_VERSION = 1;

/**
 * The resume pointer, in the field names §13.7 gives: `{v,t,p,q,i,at}`.
 *
 * Short keys are not premature optimisation here — they are the difference between a
 * 500-track queue fitting in 4096 characters and not.
 */
export interface ResumePointer {
  /** Payload version. */
  v: number;
  /** `trackId`, or `null` when nothing has played yet. */
  t: number | null;
  /** `positionMs`. */
  p: number;
  /** Queue track ids. **Absent** when the payload had to be trimmed to fit the cloud. */
  q?: number[];
  /** Queue index. */
  i: number;
  /** Unix **seconds**; the reconciliation key. */
  at: number;
}

/** The device-local snapshot: everything the pointer cannot carry. */
export interface QueueSnapshot {
  v: number;
  at: number;
  ids: number[];
  index: number;
  shuffle: boolean;
  shuffleSeed: number;
  repeatMode: RepeatMode;
  volume: number;
}

/** UI preferences. Genuinely per-device, so `localStorage` is the right and only home. */
export interface UiPrefs {
  /** The last nav tab (`ЯЩИК` · `ПОИСК` · `МОЁ`), or `null` on a first run. */
  tab: string | null;
  /** The low-ink reading toggle. `null` means "never chosen", not "off". */
  lowInk: boolean | null;
  /**
   * The chosen skin's id, or `null` for "never chosen" — which the picker shows
   * differently from "chose the default".
   *
   * Typed as a bare `string` here rather than as `lib/skin`'s `SkinId`: this slice is
   * the storage layer and must not know the registry, or `lib/skin` (which imports
   * `parsePrefs` from this file) and this module would import each other. `lib/skin`
   * validates the value against the registry on the way out.
   *
   * Unlike its two neighbours this preference is NOT per-device by nature — a person
   * who picked a skin picked it for their player, not for one phone — so `lib/skin`
   * additionally mirrors it to Telegram's `CloudStorage`. This blob stays the fast,
   * synchronous tier that the pre-boot script in `index.html` can read before the
   * bundle has parsed.
   */
  skin: string | null;
}

export const DEFAULT_PREFS: UiPrefs = Object.freeze({ tab: null, lowInk: null, skin: null });

/** What the caller hands us on every save. Live state, read at the moment of writing. */
export interface PersistenceSnapshot {
  trackId: number | null;
  positionMs: number;
  queueTrackIds: number[];
  queueIndex: number;
  shuffle: boolean;
  shuffleSeed: number;
  repeatMode: RepeatMode;
  volume: number;
  /** Drives the 10 s interval: we do not write while nothing is moving. */
  isPlaying: boolean;
}

export type PersistTier = 'server' | 'cloud' | 'device' | 'local';

/** What one `save()` actually managed to do. Every field is observable in a test. */
export interface SaveReport {
  at: number;
  flush: boolean;
  written: PersistTier[];
  skipped: PersistTier[];
  /** `false` when the server had a newer state and ignored ours (§6.6). Re-read. */
  serverApplied: boolean | null;
  /** The exact string handed to `CloudStorage`, or `null` when nothing was sent. */
  cloudValue: string | null;
  /** `true` when `q` had to be dropped to fit 4096 characters. */
  cloudTrimmed: boolean;
  errors: Partial<Record<PersistTier, ApiFailure>>;
}

export interface RestoreCandidate {
  tier: PersistTier;
  pointer: ResumePointer;
}

export interface LoadResult {
  /** The newest pointer across every tier that answered, or `null`. */
  pointer: ResumePointer | null;
  tier: PersistTier | null;
  /** The device-local queue snapshot, when it is at least as new as the pointer. */
  queue: QueueSnapshot | null;
  /**
   * The `track` payload `GET /api/playback` answered with, or `null` when the server
   * tier did not answer at all. `ResumeBanner` needs a title and a performer, and a
   * pointer carries only a `trackId` — without this the entry point would have to spend
   * a second round trip re-fetching what the playback call already returned. `null`
   * offline: the cache tiers know the id, not the name, and §12.6 forbids inventing one.
   */
  track: TrackSummary | null;
  /** Everything that answered, newest first — for diagnostics and for tests. */
  candidates: RestoreCandidate[];
}

/* ────────────────────────────────────────────────────────────────────────────
 * localStorage — a cache that is allowed to throw
 * ──────────────────────────────────────────────────────────────────────────── */

/** The two methods we use, so a test can pass a throwing double. */
export interface LocalStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * §13.7: in an iOS webview `localStorage` "can be cleared by the host app and in some
 * privacy configurations the accessor **throws** rather than returning null".
 *
 * So the *property access* is inside the `try`, not just the method call: reading
 * `window.localStorage` is itself the throwing operation in Safari's blocked-cookies
 * mode.
 */
function resolveLocalStorage(): LocalStorageLike | null {
  try {
    const storage: LocalStorageLike | undefined = globalThis.localStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

function localRead(storage: LocalStorageLike | null, key: string): string | null {
  if (storage === null) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function localWrite(storage: LocalStorageLike | null, key: string, value: string): boolean {
  if (storage === null) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    // A quota error, a private-mode throw, a host app that wiped the origin: all of them
    // mean "this cache is unavailable", none of them mean "the save failed".
    return false;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Payload construction and parsing
 * ──────────────────────────────────────────────────────────────────────────── */

export function buildPointer(snapshot: PersistenceSnapshot, atSeconds: number): ResumePointer {
  return {
    v: SNAPSHOT_VERSION,
    t: snapshot.trackId,
    p: Math.max(0, Math.round(snapshot.positionMs)),
    q: [...snapshot.queueTrackIds],
    i: snapshot.queueIndex,
    at: atSeconds,
  };
}

export function buildQueueSnapshot(
  snapshot: PersistenceSnapshot,
  atSeconds: number,
): QueueSnapshot {
  return {
    v: SNAPSHOT_VERSION,
    at: atSeconds,
    ids: [...snapshot.queueTrackIds],
    index: snapshot.queueIndex,
    shuffle: snapshot.shuffle,
    shuffleSeed: snapshot.shuffleSeed,
    repeatMode: snapshot.repeatMode,
    volume: snapshot.volume,
  };
}

/**
 * Serialise the pointer for `CloudStorage`, degrading until it fits 4096 characters.
 *
 * Returns `null` when even the trimmed pointer would not fit, which cannot happen with
 * real values and is handled anyway because the alternative is a silent Telegram-side
 * failure.
 */
export function encodeCloudPointer(pointer: ResumePointer): {
  value: string | null;
  trimmed: boolean;
} {
  const full = JSON.stringify(pointer);
  if (full.length <= CLOUD_VALUE_MAX_CHARS) return { value: full, trimmed: false };

  const { q: _dropped, ...withoutQueue } = pointer;
  const trimmed = JSON.stringify(withoutQueue);
  if (trimmed.length <= CLOUD_VALUE_MAX_CHARS) return { value: trimmed, trimmed: true };

  return { value: null, trimmed: true };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function numberIdArray(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null;
  const out: number[] = [];
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'number' || !Number.isSafeInteger(entry)) return null;
    out.push(entry);
  }
  return out;
}

/** Parse a stored pointer. An unknown shape or version yields `null` — never a guess. */
export function parsePointer(raw: string | null): ResumePointer | null {
  if (raw === null || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  if (parsed['v'] !== SNAPSHOT_VERSION) return null;

  const at = numberOrNull(parsed['at']);
  const position = numberOrNull(parsed['p']);
  const index = numberOrNull(parsed['i']);
  if (at === null || position === null || index === null) return null;

  const trackId = parsed['t'];
  if (trackId !== null && (typeof trackId !== 'number' || !Number.isSafeInteger(trackId))) {
    return null;
  }

  const pointer: ResumePointer = {
    v: SNAPSHOT_VERSION,
    t: trackId,
    p: Math.max(0, Math.round(position)),
    i: Math.round(index),
    at: Math.round(at),
  };
  const ids = parsed['q'] === undefined ? null : numberIdArray(parsed['q']);
  if (ids !== null) pointer.q = ids;
  return pointer;
}

export function parseQueueSnapshot(raw: string | null): QueueSnapshot | null {
  if (raw === null || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed['v'] !== SNAPSHOT_VERSION) return null;

  const at = numberOrNull(parsed['at']);
  const index = numberOrNull(parsed['index']);
  const ids = numberIdArray(parsed['ids']);
  if (at === null || index === null || ids === null) return null;

  const repeat = parsed['repeatMode'];
  const repeatMode: RepeatMode =
    repeat === 'off' || repeat === 'all' || repeat === 'one' ? repeat : 'off';

  return {
    v: SNAPSHOT_VERSION,
    at: Math.round(at),
    ids,
    index: Math.round(index),
    shuffle: parsed['shuffle'] === true,
    shuffleSeed: numberOrNull(parsed['shuffleSeed']) ?? 0,
    repeatMode,
    volume: Math.min(1, Math.max(0, numberOrNull(parsed['volume']) ?? 1)),
  };
}

export function parsePrefs(raw: string | null): UiPrefs {
  if (raw === null || raw.length === 0) return { ...DEFAULT_PREFS };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return { ...DEFAULT_PREFS };
  }
  if (!isRecord(parsed)) return { ...DEFAULT_PREFS };
  const tab = parsed['tab'];
  const lowInk = parsed['lowInk'];
  const skin = parsed['skin'];
  return {
    tab: typeof tab === 'string' && tab.length > 0 ? tab : null,
    lowInk: typeof lowInk === 'boolean' ? lowInk : null,
    skin: typeof skin === 'string' && skin.length > 0 ? skin : null,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The sink
 * ──────────────────────────────────────────────────────────────────────────── */

export interface PersistenceDeps {
  /** The live state, read at the instant of the write. */
  getSnapshot: () => PersistenceSnapshot;
  client?: ApiClient;
  cloud?: CloudStorageAdapter;
  device?: DeviceStorageAdapter;
  /** `null` disables the mirror; omitted resolves `globalThis.localStorage` safely. */
  local?: LocalStorageLike | null;
  /** Epoch milliseconds. */
  now?: () => number;
  cloudThrottleMs?: number;
  serverThrottleMs?: number;
  intervalMs?: number;
  /** Optional observer, for the logger. Never rendered. */
  onError?: (tier: PersistTier, failure: ApiFailure) => void;
}

export interface SaveOptions {
  /** A flush ignores every throttle: `pause`, `ended`, `deactivated`, hidden, `pagehide`. */
  flush?: boolean;
}

/**
 * The two event targets the flush points live on. Injectable so a test can drive both
 * with one recording double, and so a non-DOM environment simply gets `null`.
 */
export interface DocumentLike extends EventTarget {
  readonly visibilityState?: string;
}

export interface FlushScope {
  /** Where `pagehide` is listened for. Defaults to `globalThis`. */
  window?: EventTarget;
  /** Where `visibilitychange` is listened for. Defaults to `globalThis.document`. */
  document?: DocumentLike | null;
}

/** Reading `globalThis.document` is safe, but it is absent in a worker or in Node. */
function readDocument(): DocumentLike | null {
  const doc: DocumentLike | undefined = globalThis.document;
  return doc ?? null;
}

export interface Persistence {
  save(options?: SaveOptions): Promise<SaveReport>;
  load(): Promise<LoadResult>;
  readPrefs(): UiPrefs;
  writePrefs(patch: Partial<UiPrefs>): UiPrefs;
  /** Binds `visibilitychange`→hidden, `pagehide` and the 10 s while-playing interval. */
  installFlushListeners(scope?: FlushScope): () => void;
  dispose(): void;
}

export function createPersistence(deps: PersistenceDeps): Persistence {
  const client = deps.client ?? api;
  const cloud = deps.cloud ?? telegram.cloudStorage;
  const device = deps.device ?? telegram.deviceStorage;
  const local = deps.local === undefined ? resolveLocalStorage() : deps.local;
  const now = deps.now ?? (() => Date.now());
  const cloudThrottleMs = deps.cloudThrottleMs ?? CLOUD_THROTTLE_MS;
  const serverThrottleMs = deps.serverThrottleMs ?? SERVER_THROTTLE_MS;
  const intervalMs = deps.intervalMs ?? FLUSH_INTERVAL_MS;

  let lastCloudAt = 0;
  let lastServerAt = 0;
  let lastDeviceValue = '';
  let disposed = false;

  const report = (tier: PersistTier, error: unknown, into: SaveReport): void => {
    const failure = toApiFailure(error);
    into.errors[tier] = failure;
    deps.onError?.(tier, failure);
  };

  async function save(options: SaveOptions = {}): Promise<SaveReport> {
    const flush = options.flush === true;
    const millis = now();
    const atSeconds = Math.floor(millis / 1000);
    const snapshot = deps.getSnapshot();
    const pointer = buildPointer(snapshot, atSeconds);

    const result: SaveReport = {
      at: atSeconds,
      flush,
      written: [],
      skipped: [],
      serverApplied: null,
      cloudValue: null,
      cloudTrimmed: false,
      errors: {},
    };
    if (disposed) {
      result.skipped.push('local', 'cloud', 'device', 'server');
      return result;
    }

    // ── local: synchronous, unthrottled, and FIRST. `pagehide` may not come back.
    if (localWrite(local, RESUME_KEY, JSON.stringify(pointer))) result.written.push('local');
    else result.skipped.push('local');

    // ── cloud: the resume pointer only, hard-capped at 4096 characters.
    const encoded = encodeCloudPointer(pointer);
    result.cloudTrimmed = encoded.trimmed;
    const cloudDue = flush || millis - lastCloudAt >= cloudThrottleMs;
    if (!cloudDue || !cloud.available || encoded.value === null) {
      result.skipped.push('cloud');
    } else {
      const value = encoded.value;
      lastCloudAt = millis;
      result.cloudValue = value;
      try {
        if (await cloud.setItem(RESUME_KEY, value)) result.written.push('cloud');
        else result.skipped.push('cloud');
      } catch (error) {
        report('cloud', error, result);
      }
    }

    // ── device: the full queue, written only when it actually changed.
    const queueSnapshot = buildQueueSnapshot(snapshot, atSeconds);
    const queueValue = JSON.stringify(queueSnapshot);
    // `at` moves on every save, so it is stripped before comparing: an unchanged queue
    // must not produce a 5 MB tier write every ten seconds.
    const queueChanged = comparableQueue(queueValue) !== comparableQueue(lastDeviceValue);
    if (!device.available || !(queueChanged || flush)) {
      result.skipped.push('device');
    } else {
      lastDeviceValue = queueValue;
      try {
        if (await device.setItem(QUEUE_KEY, queueValue)) result.written.push('device');
        else result.skipped.push('device');
      } catch (error) {
        report('device', error, result);
      }
    }

    // ── server: the source of truth, but rate-limited to 60/min (§6.6).
    const serverDue = flush || millis - lastServerAt >= serverThrottleMs;
    if (!serverDue || snapshot.trackId === null) {
      result.skipped.push('server');
    } else {
      lastServerAt = millis;
      try {
        const answer = await client.putPlayback({
          trackId: snapshot.trackId,
          positionMs: pointer.p,
          queueIndex: snapshot.queueIndex,
          shuffle: snapshot.shuffle,
          shuffleSeed: snapshot.shuffleSeed,
          repeatMode: snapshot.repeatMode,
          volume: snapshot.volume,
        });
        result.serverApplied = answer.applied;
        result.written.push('server');
      } catch (error) {
        report('server', error, result);
      }
    }

    return result;
  }

  /** `at` moves on every save; it must not count as a change of the queue itself. */
  function comparableQueue(value: string): string {
    return value.replace(/"at":\d+,?/u, '');
  }

  async function load(): Promise<LoadResult> {
    const candidates: RestoreCandidate[] = [];

    const localPointer = parsePointer(localRead(local, RESUME_KEY));
    if (localPointer !== null) candidates.push({ tier: 'local', pointer: localPointer });

    if (cloud.available) {
      try {
        const cloudPointer = parsePointer(await cloud.getItem(RESUME_KEY));
        if (cloudPointer !== null) candidates.push({ tier: 'cloud', pointer: cloudPointer });
      } catch {
        // An unavailable cache tier is not an error worth surfacing: the server tier and
        // the local mirror both still answer.
      }
    }

    let queue: QueueSnapshot | null = null;
    if (device.available) {
      try {
        queue = parseQueueSnapshot(await device.getItem(QUEUE_KEY));
        if (queue !== null) {
          candidates.push({
            tier: 'device',
            pointer: {
              v: SNAPSHOT_VERSION,
              t: null,
              p: 0,
              q: [...queue.ids],
              i: queue.index,
              at: queue.at,
            },
          });
        }
      } catch {
        queue = null;
      }
    }

    let serverTrack: TrackSummary | null = null;
    try {
      const playback = await client.playback();
      serverTrack = playback.track;
      candidates.push({
        tier: 'server',
        pointer: {
          v: SNAPSHOT_VERSION,
          t: playback.trackId,
          p: playback.positionMs,
          i: playback.queueIndex,
          at: playback.updatedAt,
        },
      });
    } catch {
      // Offline, or not authenticated yet. The cache tiers are exactly what this case is
      // for; `load()` reports what it has rather than failing the boot.
    }

    // §13.7: "take the snapshot with the newest `at` across all tiers". A tie prefers the
    // server, then the cloud, then the device, then the local mirror — most trustworthy
    // first, so a same-second tie never resolves to a cache.
    const rank: Record<PersistTier, number> = { server: 0, cloud: 1, device: 2, local: 3 };
    const sorted = [...candidates].sort(
      (a, b) => b.pointer.at - a.pointer.at || rank[a.tier] - rank[b.tier],
    );

    // A pointer with no track is not a resume; it is an empty slot. `device` never
    // carries one, which is why it is skipped when choosing the winner.
    const winner = sorted.find((entry) => entry.tier !== 'device' && entry.pointer.t !== null);
    const chosen = winner ?? sorted.find((entry) => entry.tier !== 'device') ?? null;

    return {
      pointer: chosen?.pointer ?? null,
      tier: chosen?.tier ?? null,
      queue,
      // Only when the winning pointer is the one the server answered with: a cache tier
      // that outranks the server describes a DIFFERENT, newer track, and pairing its id
      // with the server's title would print the wrong name in the banner.
      track: chosen?.tier === 'server' ? serverTrack : null,
      candidates: sorted,
    };
  }

  function readPrefs(): UiPrefs {
    return parsePrefs(localRead(local, PREFS_KEY));
  }

  function writePrefs(patch: Partial<UiPrefs>): UiPrefs {
    const next: UiPrefs = { ...readPrefs(), ...patch };
    localWrite(local, PREFS_KEY, JSON.stringify(next));
    return next;
  }

  function installFlushListeners(scope: FlushScope = {}): () => void {
    const win = scope.window ?? globalThis;
    const doc = scope.document ?? readDocument();

    // `flush` here is a save, never a media call: §13.3's first negative rule is that
    // nothing driven by page visibility may touch the audio element. There is no
    // `pause` in this function and there never may be.
    const flushNow = (): void => {
      void save({ flush: true });
    };

    const onVisibility = (): void => {
      // Read through `visibilityState`, never through the boolean `hidden` flag: the
      // Android minimise path fires this event and playback must survive it untouched.
      // Nothing in this handler may reach the audio element (§13.3, rule 1).
      if (doc?.visibilityState === 'hidden') flushNow();
    };

    const onPageHide = (): void => {
      flushNow();
    };

    // Registered on the document alone: `visibilitychange` bubbles to the window, so
    // listening on both would flush twice for one event.
    doc?.addEventListener('visibilitychange', onVisibility);
    win.addEventListener('pagehide', onPageHide);

    // §13.7's 10 s interval. A timer, not rAF: timers keep running while the page is
    // hidden and rAF does not, which is precisely the state this interval exists for.
    const timer = setInterval(() => {
      if (deps.getSnapshot().isPlaying) void save();
    }, intervalMs);

    return () => {
      clearInterval(timer);
      doc?.removeEventListener('visibilitychange', onVisibility);
      win.removeEventListener('pagehide', onPageHide);
    };
  }

  return {
    save,
    load,
    readPrefs,
    writePrefs,
    installFlushListeners,
    dispose(): void {
      disposed = true;
    },
  };
}
